import { Box, Card, CardActionArea, CardContent, Typography, alpha } from '@mui/material';
import { green, orange } from '@mui/material/colors';
import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TypeChip from '../chip/TypeChip';
import DueMessage from '../due-message/DueMessage';
import ExerciseType from '../exercise-type/ExerciseType';
import { ExerciseState, ExerciseSummary, SimulatorType } from '../types';
import { formatDateTime } from '../utils/formatTime';
import { getKSim } from '../utils/ksim';

export type ExerciseCardProps = ExerciseSummary & {
  exerciseState?: ExerciseState;
  onAction?: ReactNode;
  showCompletionTime?: boolean;
  showKSim?: boolean;
  showTimeDetails?: boolean;
  to: string;
};

const ExerciseCard = ({
  adHoc,
  assigned,
  endTime,
  exerciseState,
  onAction,
  showCompletionTime,
  showKSim = true,
  showTimeDetails,
  simulatorType,
  startTime,
  title,
  to,
  type,
}: ExerciseCardProps) => {
  const ksim = getKSim(type, simulatorType);

  return (
    <Card>
      <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', gap: 2, height: '100%' }}>
          {showKSim && (
            <Box
              sx={theme => ({
                backgroundColor: adHoc
                  ? orange[100]
                  : simulatorType == SimulatorType.Cloud
                  ? alpha(theme.palette.primary.main, 0.3)
                  : alpha(green[800], 0.12),
                p: 3,
                borderRadius: 1,
                display: { xs: 'none', sm: 'grid' },
                placeContent: 'center',
                flexShrink: 0,
              })}
            >
              <Box component="img" width={85} src={ksim} />
            </Box>
          )}
          <Box flex="1" display="flex" flexDirection="column">
            <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex', mb: 1 }}>
              <ExerciseType type={type} />
              {assigned && (
                <>
                  <Box flex="1"></Box>
                  <TypeChip type={'assigned'} />
                </>
              )}
              <TypeChip type={simulatorType} />
              {exerciseState === ExerciseState.Active && onAction && !assigned && (
                <>
                  <Box flex="1"></Box>
                  {onAction}
                </>
              )}
            </Box>
            <Typography component="h3" variant="h6" title={title} sx={{ mb: adHoc ? 1 : 2 }}>
              {title.length > 50 ? title.substring(0, 47) + '...' : title}
            </Typography>
            {adHoc && (
              <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                Simulate now
              </Typography>
            )}

            <Box flex="1"></Box>

            {showTimeDetails && (
              <>
                <Typography color="text.secondary" variant="body2" paragraph>
                  Start: {formatDateTime(startTime)}
                  {!adHoc && (
                    <>
                      <br />
                      End: {endTime ? formatDateTime(endTime) : '-'}
                    </>
                  )}
                </Typography>
                {exerciseState === ExerciseState.Active && <DueMessage endTime={endTime} />}
              </>
            )}
            {showCompletionTime && (
              <Typography color="text.secondary" variant="body2">
                Completed: {endTime ? formatDateTime(endTime) : '-'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ExerciseCard;
